import type { Observable } from 'rxjs';

import type {
  Money,
  OverviewActivityStaticInfo,
  OverviewDestinationStaticInfo,
} from '../general';
import type { TransportStaticInfo } from '../semboEndpoints/transport';

import type {
  ActivityIngredientResponse,
  AlternativeActivityIngredientResponse,
  AvailableActivityIngredientResponse,
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  AvailableHotelIngredientResponse,
  FerryIngredientResponse,
  FlightIngredientResponse,
  HotelIngredientResponse,
  OwnStayIngredientResponse,
  OwnTransportIngredientResponse,
  TransportAlternative,
  UnavailableActivityIngredientResponse,
  UnavailableFlightIngredientResponse,
  UnavailableHotelIngredientResponse,
  UnvailableFerryIngredientResponse,
} from './ingredientResponses';

export enum ConnectionType {
  NONE,
  CAR,
  WALK,
  BICYCLE,
  FLIGHT,
  FERRY,
}

export type RecipeResponse = Observable<ItineraryResponse>;

export type ItineraryResponse = {
  date: Date;
  itinerary: Itinerary;
  prices: ItineraryPrices;
  isSearched: boolean;
  isSuggested: boolean;
  key: string;
};

export interface ItineraryPrices {
  totalPriceWithAlternative: Money | null;
  totalPrice: Money | null;
  grossPrice: Money | null;
  grossPriceWithAlternative: Money | null;
  pricePerPerson: Money | null;
  grossPricePerPerson: Money | null;
  pricePerPersonWithAlternative: Money | null;
  grossPriceWithAlternativePerPerson: Money | null;
}

export type Itinerary = Array<
  Observable<
    | OwnStayIngredientResponse
    | HotelIngredientResponse
    | FlightIngredientResponse
    | FerryIngredientResponse
    | OwnTransportIngredientResponse
  >
>;

export interface ItineraryWithPrice {
  key: string;
  totalPrice: Money | null;
  totalPriceWithAlternative: Money | null;
  grossPrice: Money | null;
  grossPriceWithAlternative: Money | null;
  pricePerPerson: Money | null;
  grossPricePerPerson: Money | null;
  pricePerPersonWithAlternative: Money | null;
  grossPriceWithAlternativePerPerson: Money | null;
  mostPopularPrice: Money | null;
  cheapestPrice: Money | null;
  date: Date;
  itinerary: ItineraryMainIngredientResponse[];
  ownStayOnlyItinerary: OwnStayIngredientResponse[];
  hotelOnlyItinerary: HotelIngredientResponse[];
  flightOnlyItinerary: FlightIngredientResponse[];
  ferryOnlyItinerary: FerryIngredientResponse[];
  isSearched: boolean;
  isSuggested: boolean;
}

export interface CompleteItineraryWithPrice extends ItineraryWithPrice {
  totalPrice: Money;
}

export interface ItineraryWithStaticInfo extends ItineraryWithPrice {
  itinerary: (
    | OwnStayIngredientResponseWithStaticInfo
    | HotelIngredientResponseWithStaticInfo
    | FlightIngredientResponseWithStaticInfo
    | FerryIngredientResponseWithStaticInfo
    | OwnTransportIngredientResponseWithStaticInfo
  )[];
  ownStayOnlyItinerary: OwnStayIngredientResponseWithStaticInfo[];
  hotelOnlyItinerary: HotelIngredientResponseWithStaticInfo[];
}

export type OwnStayIngredientResponseWithStaticInfo =
  OwnStayIngredientResponse & {
    transportToBeReached: ConnectionType;
    staticInfo: null;
    subIngredientResponseWithStaticInfo: ActivityIngredientResponseWithStaticInfo[];
  };

export type OwnTransportIngredientResponseWithStaticInfo =
  OwnTransportIngredientResponse & {
    staticInfo: null;
  };

export type HotelIngredientResponseWithStaticInfo =
  | AvailableHotelIngredientResponseWithStaticInfo
  | UnavailableHotelIngredientResponseWithStaticInfo;

export type AvailableHotelIngredientResponseWithStaticInfo =
  AvailableHotelIngredientResponse & {
    transportToBeReached: ConnectionType;
    staticInfo: OverviewDestinationStaticInfo;
    suggestionsStaticInfo: OverviewDestinationStaticInfo[];
    subIngredientResponseWithStaticInfo: ActivityIngredientResponseWithStaticInfo[];
  };

export type UnavailableHotelIngredientResponseWithStaticInfo =
  UnavailableHotelIngredientResponse & {
    transportToBeReached: ConnectionType;
    staticInfo: OverviewDestinationStaticInfo | undefined;
    suggestionsStaticInfo: OverviewDestinationStaticInfo[];
    subIngredientResponseWithStaticInfo: ActivityIngredientResponseWithStaticInfo[];
  };

export type ActivityIngredientResponseWithStaticInfo =
  | AvailableActivityIngredientResponseWithStaticInfo
  | UnavailableActivityIngredientResponseWithStaticInfo
  | AlternativeActivityIngredientResponseWithStaticInfo;

export type AvailableActivityIngredientResponseWithStaticInfo =
  AvailableActivityIngredientResponse & {
    staticInfo: OverviewActivityStaticInfo;
  };

export type UnavailableActivityIngredientResponseWithStaticInfo =
  UnavailableActivityIngredientResponse & {
    staticInfo: undefined;
  };

export type AlternativeActivityIngredientResponseWithStaticInfo =
  AlternativeActivityIngredientResponse & {
    staticInfo: OverviewActivityStaticInfo;
  };

export type FlightIngredientResponseWithStaticInfo =
  | AvailableFlightIngredientResponseWithStaticInfo
  | UnavailableFlightIngredientResponseWithStaticInfo;

export type AvailableFlightIngredientResponseWithStaticInfo =
  AvailableFlightIngredientResponse & {
    staticInfo: TransportStaticInfo;
  };

export type AlternativeTransportIngredientResponseWithStaticInfo =
  | AlternativeFlightIngredientResponseWithStaticInfo
  | AlternativeFerryIngredientResponseWithStaticInfo;

export type AlternativeFlightIngredientResponseWithStaticInfo =
  UnavailableFlightIngredientResponseWithStaticInfo & {
    alternativeSuggestion: TransportAlternative;
    staticInfo: TransportStaticInfo;
  };

export type UnavailableFlightIngredientResponseWithStaticInfo =
  UnavailableFlightIngredientResponse & {
    staticInfo: TransportStaticInfo | undefined;
  };

export type FerryIngredientResponseWithStaticInfo =
  | AvailableFerryIngredientResponseWithStaticInfo
  | UnavailableFerryIngredientResponseWithStaticInfo;

export type AvailableFerryIngredientResponseWithStaticInfo =
  AvailableFerryIngredientResponse & {
    staticInfo: TransportStaticInfo;
  };

export type UnavailableFerryIngredientResponseWithStaticInfo =
  UnvailableFerryIngredientResponse & {
    staticInfo: TransportStaticInfo | undefined;
  };

export type AlternativeFerryIngredientResponseWithStaticInfo =
  UnavailableFerryIngredientResponseWithStaticInfo & {
    alternativeSuggestion: TransportAlternative;
    staticInfo: TransportStaticInfo;
  };

export interface ChronologicalItinerary {
  startDate: Date;
  datePeriod?: string;
  dayPeriod?: string;
  shortDatePeriod?: string;
  numberOfNights: number;
  ingredients: ItineraryMainIngredientResponseWithStaticInfo[];
}

export type TransportIngredientResponseWithStaticInfo =
  | FlightIngredientResponseWithStaticInfo
  | FerryIngredientResponseWithStaticInfo;

export type AvailableTransportIngredientResponseWithStaticInfo =
  | AvailableFlightIngredientResponseWithStaticInfo
  | AvailableFerryIngredientResponseWithStaticInfo;

export type ItineraryMainIngredientResponse =
  | FerryIngredientResponse
  | FlightIngredientResponse
  | HotelIngredientResponse
  | OwnStayIngredientResponse
  | OwnTransportIngredientResponse;

export type ItineraryMainIngredientResponseWithStaticInfo =
  | FerryIngredientResponseWithStaticInfo
  | FlightIngredientResponseWithStaticInfo
  | HotelIngredientResponseWithStaticInfo
  | OwnStayIngredientResponseWithStaticInfo
  | OwnTransportIngredientResponseWithStaticInfo;

export type ItineraryTrasnportIngredientResponseWithStaticInfo =
  | FerryIngredientResponseWithStaticInfo
  | FlightIngredientResponseWithStaticInfo;

export type ItineraryIngredientResponse =
  | ItineraryMainIngredientResponse
  | ActivityIngredientResponse;
